import React, { useState } from 'react';
import { useStore } from '../store/useStore';
import { Send, Video, UserCircle2, Flag, Smile, Paperclip, Download, FileIcon } from 'lucide-react';
import { VideoChat } from './VideoChat';
import { ReportModal } from './ReportModal';
import { EmojiPicker } from './EmojiPicker';
import { FileUpload } from './FileUpload';
import { ThemeToggle } from './ThemeToggle';
import { socket, searchNewPartner } from '../services/socket';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from './LanguageSwitcher';

export const ChatInterface: React.FC = () => {
  const { t } = useTranslation();
  const [message, setMessage] = useState('');
  const [showVideo, setShowVideo] = useState(false);
  const [showReportModal, setShowReportModal] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showFileUpload, setShowFileUpload] = useState(false);
  const { user, currentPartner, messages, addMessage } = useStore();

  const sendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || !currentPartner) return;

    const newMessage = {
      id: Math.random().toString(36).substr(2, 9),
      senderId: user!.id,
      content: message,
      timestamp: Date.now(),
      type: 'text' as const,
    };

    addMessage(newMessage);

    socket.emit('message', {
      to: currentPartner.socketId,
      message: message,
      type: 'text'
    });

    setMessage('');
  };

  const handleNewSearch = () => {
    setShowVideo(false);
    searchNewPartner();
  };

  const handleReport = (reason: string, details: string) => {
    console.log('Reportando usuário:', { reason, details, partner: currentPartner });
    alert(`${t('chat.reported')}\n${t('chat.reason')}: ${reason}`);
  };

  const handleEmojiSelect = (emoji: string) => {
    setMessage(message + emoji);
    setShowEmojiPicker(false);
  };

  const handleFileDownload = (fileName: string, fileData: string, _fileType: string) => {
    const link = document.createElement('a');
    link.href = fileData;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const renderMessage = (msg: typeof messages[0]) => {
    if (msg.type === 'file' && msg.fileName) {
      const isImage = msg.fileType?.startsWith('image/');
      return (
        <div className="file-message">
          {isImage && msg.fileData ? (
            <img
              src={msg.fileData}
              alt={msg.fileName}
              className="max-w-full max-h-48 rounded-lg mb-2 cursor-pointer"
              onClick={() => handleFileDownload(msg.fileName!, msg.fileData!, msg.fileType!)}
            />
          ) : (
            <div className="flex items-center space-x-3 p-2 bg-black/20 rounded-lg mb-2">
              <FileIcon className="w-10 h-10 text-gray-400" />
              <div className="flex-1 min-w-0">
                <p className="truncate font-medium">{msg.fileName}</p>
                <p className="text-sm opacity-70">{formatFileSize(msg.fileSize)}</p>
              </div>
            </div>
          )}
          <button
            onClick={() => handleFileDownload(msg.fileName!, msg.fileData!, msg.fileType!)}
            className="flex items-center text-sm hover:underline"
          >
            <Download className="w-4 h-4 mr-1" />
            {t('file.download') || 'Baixar'}
          </button>
        </div>
      );
    }
    return msg.content;
  };

  return (
    <div className="min-h-screen bg-black flex chat-container">
      <div className="flex-1 flex flex-col max-w-6xl mx-auto p-4 w-full">
        <div className="bg-yellow-400 p-4 rounded-t-lg flex items-center justify-between chat-header">
          <div className="flex items-center space-x-3">
            <UserCircle2 className="w-10 h-10" />
            <div>
              <h2 className="font-bold text-black">
                {currentPartner?.name || t('profile.finding_partner')}
              </h2>
              <p className="text-sm text-gray-700">
                {currentPartner ? t('profile.online') : t('profile.searching')}
              </p>
            </div>
          </div>
          <div className="flex space-x-2 md:space-x-4">
            <LanguageSwitcher />
            <ThemeToggle />
            {currentPartner && (
              <>
                <button
                  onClick={() => setShowVideo(!showVideo)}
                  className="p-2 hover:bg-yellow-500 rounded-full"
                  title={t('chat.video_call') || 'Chamada de vídeo'}
                >
                  <Video className="w-6 h-6" />
                </button>
                <button
                  onClick={() => setShowReportModal(true)}
                  className="p-2 hover:bg-yellow-500 rounded-full"
                  title={t('chat.report_user') || 'Denunciar'}
                >
                  <Flag className="w-6 h-6" />
                </button>
                <button
                  onClick={handleNewSearch}
                  className="bg-black text-yellow-400 px-3 py-2 rounded-lg hover:bg-gray-800 text-sm md:text-base md:px-4 md:py-2"
                >
                  {t('chat.new_search')}
                </button>
              </>
            )}
          </div>
        </div>

        {showVideo && currentPartner && <VideoChat />}

        <div className="flex-1 bg-gray-900 p-4 overflow-y-auto space-y-4 chat-messages">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={clsx(
                'max-w-[80%] p-3 rounded-lg fade-in',
                msg.senderId === user?.id
                  ? 'ml-auto bg-yellow-400 text-black'
                  : 'bg-gray-700 text-white'
              )}
            >
              {renderMessage(msg)}
            </div>
          ))}
        </div>

        <form
          onSubmit={sendMessage}
          className="bg-yellow-400 p-4 rounded-b-lg flex items-center space-x-2 md:space-x-4"
        >
          <button
            type="button"
            onClick={() => setShowEmojiPicker(true)}
            className="p-2 text-black hover:bg-yellow-500 rounded-full"
            title={t('chat.emoji') || 'Emoji'}
          >
            <Smile className="w-6 h-6" />
          </button>
          <button
            type="button"
            onClick={() => setShowFileUpload(true)}
            className="p-2 text-black hover:bg-yellow-500 rounded-full"
            disabled={!currentPartner}
            title={t('file.attach') || 'Anexar arquivo'}
          >
            <Paperclip className="w-6 h-6" />
          </button>
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t('chat.placeholder')}
            className="flex-1 p-2 rounded border-2 border-black focus:outline-none focus:border-black"
            disabled={!currentPartner}
          />
          <button
            type="submit"
            className="bg-black text-yellow-400 p-2 rounded-full hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!currentPartner}
          >
            <Send className="w-6 h-6" />
          </button>
        </form>
        <ReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          onReport={handleReport}
        />
        <EmojiPicker
          isOpen={showEmojiPicker}
          onClose={() => setShowEmojiPicker(false)}
          onEmojiSelect={handleEmojiSelect}
        />
        <FileUpload
          isOpen={showFileUpload}
          onClose={() => setShowFileUpload(false)}
        />
      </div>
    </div>
  );
};
